import { useEffect, useState } from 'preact/hooks';
import { registry } from '../../sim/registry';
import { skillIcon } from '../icons';
import { api } from './api';
import { ui } from './store';

const CLASS_ORDER = ['ravager', 'stormweaver', 'arsenalist', 'windfist', 'cantor', 'fiendbinder'];

function classes() {
  return CLASS_ORDER.map((id) => registry.classes.get(id)).filter((c): c is NonNullable<typeof c> => !!c);
}

export function TitleScreen() {
  void ui.version.value;
  const hasChars = api().account.characters.length > 0;
  return (
    <div class="screen title-screen interactive">
      <div class="title-art" />
      <div class="title-block">
        <h1 class="game-title">AETHERFALL</h1>
        <div class="title-rule" />
        <p class="tagline">The Lantern is shattered. The Hollow stirs.</p>
        <div class="title-buttons">
          <button class="btn btn--gold big" onClick={() => (ui.screen.value = hasChars ? 'select' : 'create')}>
            {hasChars ? 'Continue' : 'Begin your journey'}
          </button>
          {hasChars && (
            <button class="btn big" onClick={() => (ui.screen.value = 'create')}>
              New character
            </button>
          )}
        </div>
        <p class="fine">An original action RPG inspired by Lost Ark. All names, art and audio are original.</p>
      </div>
    </div>
  );
}

export function CharacterSelect() {
  void ui.version.value;
  const a = api().account;
  const [sel, setSel] = useState<string | null>(a.lastCharacterId ?? a.characters[0]?.id ?? null);
  const [confirmDel, setConfirmDel] = useState(false);
  const [importing, setImporting] = useState(false);
  const [code, setCode] = useState('');
  const [err, setErr] = useState('');
  const chosen = a.characters.find((c) => c.id === sel);
  useEffect(() => {
    ui.showcaseClass.value = chosen?.classId ?? null;
  }, [chosen?.classId]);
  return (
    <div class="screen select-screen interactive">
      <div class="select-list panel">
        <div class="panel__title">Roster</div>
        <div class="roster">
          {a.characters.map((c) => {
            const cls = registry.classes.get(c.classId);
            return (
              <button key={c.id} class={`roster-row${c.id === sel ? ' active' : ''}`} onClick={() => setSel(c.id)} onDblClick={() => void api().playCharacter(c.id)}>
                <span class="crest-dot" style={{ background: `#${(cls?.hue ?? 0x888888).toString(16).padStart(6, '0')}` }} />
                <span class="rn">{c.name}</span>
                <span class="rc">{cls?.name ?? c.classId}</span>
                <span class="rl">Lv.{c.level}</span>
              </button>
            );
          })}
          {a.characters.length < 8 && (
            <button class="roster-row new" onClick={() => (ui.screen.value = 'create')}>
              + Create character
            </button>
          )}
        </div>
        <div class="select-foot">
          <button class="btn" onClick={() => setImporting(!importing)}>
            Import / Export
          </button>
          {chosen && !confirmDel && (
            <button class="btn" onClick={() => setConfirmDel(true)}>
              Delete
            </button>
          )}
          {chosen && confirmDel && (
            <button
              class="btn"
              style={{ borderColor: '#e5484d', color: '#ff9a9a' }}
              onClick={() => {
                api().deleteCharacter(chosen.id);
                setSel(null);
                setConfirmDel(false);
              }}
            >
              Confirm delete {chosen.name}
            </button>
          )}
        </div>
        {importing && (
          <div class="import-box">
            <textarea value={code} onInput={(e) => setCode((e.target as HTMLTextAreaElement).value)} placeholder="Paste a save code, or press Export to copy yours." rows={4} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button
                class="btn"
                onClick={() => {
                  const c = api().exportSave();
                  setCode(c);
                  void navigator.clipboard?.writeText(c).catch(() => {});
                }}
              >
                Export
              </button>
              <button
                class="btn btn--gold"
                onClick={() => {
                  try {
                    api().importSave(code);
                    setErr('');
                    setImporting(false);
                  } catch (e) {
                    setErr(e instanceof Error ? e.message : String(e));
                  }
                }}
              >
                Import
              </button>
            </div>
            {err && <div style={{ color: '#ff8a8a' }}>{err}</div>}
          </div>
        )}
      </div>
      {chosen && (
        <div class="select-detail">
          <h2>{chosen.name}</h2>
          <p class="sub">
            {registry.classes.get(chosen.classId)?.name} · Level {chosen.level}
          </p>
          <button class="btn btn--gold big" onClick={() => void api().playCharacter(chosen.id)}>
            Enter the Sundered Isles
          </button>
        </div>
      )}
      <button class="btn back-btn" onClick={() => (ui.screen.value = 'title')}>
        Back
      </button>
    </div>
  );
}

export function CharacterCreate() {
  const list = classes();
  const [clsId, setCls] = useState(list[0]?.id ?? 'ravager');
  const [name, setName] = useState('');
  const [err, setErr] = useState('');
  const cls = registry.classes.get(clsId);
  const hasChars = api().account.characters.length > 0;
  useEffect(() => {
    ui.showcaseClass.value = clsId;
  }, [clsId]);
  const submit = () => {
    const n = name.trim();
    if (n.length < 2 || n.length > 16) return setErr('Name must be 2–16 characters.');
    if (!/^[\p{L}\p{N} '_-]+$/u.test(n)) return setErr('Letters, numbers, spaces, apostrophes and dashes only.');
    const id = api().createCharacter(n, clsId);
    void api().playCharacter(id);
  };
  return (
    <div class="screen create-screen interactive">
      <div class="class-grid">
        {list.map((c) => (
          <button key={c.id} class={`class-card${c.id === clsId ? ' active' : ''}`} onClick={() => setCls(c.id)} style={{ ['--hue' as string]: `#${c.hue.toString(16).padStart(6, '0')}` }}>
            <div class="cc-name">{c.name}</div>
            <div class="cc-title">{c.title}</div>
            <div class="cc-role">{c.role === 'support' ? 'Support' : 'Damage'}</div>
          </button>
        ))}
      </div>
      {cls && (
        <div class="class-detail panel">
          <div class="panel__title">{cls.name}</div>
          <div style={{ padding: '10px 14px' }}>
            <p class="cd-arch">{cls.archetype}</p>
            <p>{cls.description}</p>
            <div class="section-h">{cls.identityName}</div>
            <p class="cd-id">{cls.identityDescription}</p>
            <div class="section-h">Skills</div>
            <div class="cd-skills">
              {cls.skills.slice(0, 8).map((s) => (
                <img key={s.id} src={skillIcon(s, cls.hue)} title={`${s.name}: ${s.description}`} alt={s.name} />
              ))}
              {cls.awakenings[0] && <img class="apex" src={skillIcon(cls.awakenings[0], cls.hue)} title={`${cls.awakenings[0].name}: ${cls.awakenings[0].description}`} alt="" />}
            </div>
            <div class="name-row">
              <input
                class="name-input"
                maxLength={16}
                placeholder="Character name"
                value={name}
                onInput={(e) => setName((e.target as HTMLInputElement).value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') submit();
                }}
              />
              <button class="btn btn--gold" onClick={submit}>
                Create
              </button>
            </div>
            {err && <div class="err">{err}</div>}
          </div>
        </div>
      )}
      <button class="btn back-btn" onClick={() => (ui.screen.value = hasChars ? 'select' : 'title')}>
        Back
      </button>
    </div>
  );
}

export function LoadingScreen() {
  return (
    <div class="screen loading-screen">
      <div class="loading-block">
        <div class="loading-title">{ui.loadingText.value}</div>
        <div class="loading-bar">
          <i style={{ transform: `scaleX(${ui.loadingProgress.value})` }} />
        </div>
        <p class="tip">Tip: back attacks deal +5% damage and gain +10% crit chance; head attacks add +20% damage and stagger.</p>
      </div>
    </div>
  );
}
